<template>
    <div class="cms-document text-base">
        <div @click="handleClick" v-html="document.mainHtml"></div>
        <slot></slot>
        <nav v-if="document.previousLink || document.nextLink" class="py-4 mt-8 max-w-3xl flex gap-8 border-t">
            <NuxtLink v-if="document.previousLink" :to="document.previousLink.path" class="flex gap-1 items-baseline">
                <Icon name="arrow-left" />
                <span>{{ document.previousLink.title }}</span>
            </NuxtLink>
            <NuxtLink v-if="document.nextLink" :to="document.nextLink.path" class="flex gap-1 items-baseline ml-auto">
                <span>{{ document.nextLink.title }}</span>
                <Icon name="arrow-right" />
            </NuxtLink>
        </nav>
    </div>
</template>
<script setup lang="ts">
const { document } = defineProps<{ document: CMSDocument }>();

// links that start with these prefixes will be ignored by the click handler
const CLICK_IGNORE_PREFIXES = ["#", "/inventoryservice", "/admin"];

function handleClick(e: MouseEvent) {
    const target = e.target as HTMLElement;

    if (!target) return;

    const anchor = target.closest("a");
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    /**
     * Allows hash-based navigation (e.g., #section-id) and external links to function normally
     * without being intercepted by the router. This regex pattern typically matches:
     * - Anchor links starting with # (internal page navigation)
     * - External URLs with protocols (http://, https://, etc.)
     * - Email links (mailto:)
     * - Phone links (tel:)
     *
     * Links matching this pattern will bypass client-side routing and use default browser behavior,
     * ensuring proper navigation within the CMS document and to external resources.
     */
    if (
        !href ||
        CLICK_IGNORE_PREFIXES.some((path) => href.startsWith(path)) ||
        /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(href)
    ) {
        return;
    }

    // Respect modified clicks and non-left-button clicks
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
    }

    e.preventDefault();
    return navigateTo(href);
}
</script>
<style>
.cms-document {
    ol,
    ul {
        padding-left: calc(var(--spacing) * 8);
    }

    ul {
        list-style: disc;
    }

    ol {
        list-style: numbers;
    }

    h1,
    h2,
    h3,
    h4 {
        scroll-margin-top: calc(var(--spacing) * 22);
        img {
            display: inline-block;
        }
    }

    h2,
    ol,
    ul,
    p {
        line-height: var(--leading-relaxed);
        margin-bottom: calc(var(--spacing) * 4);
    }

    h3 {
        margin: calc(var(--spacing) * 4) 0;
    }

    table {
        border-collapse: collapse;
        thead tr {
            background-color: var(--color-gray-200);
            text-align: left;
        }
        th,
        td {
            padding: calc(var(--spacing) * 2);
            border: 1px solid var(--color-gray-200);
        }
        tbody tr:nth-child(even) {
            background-color: var(--color-gray-100);
        }
    }

    code {
        color: var(--color-pink-500);
        font-size: 0.9rem;
        background-color: var(--color-gray-100);
        padding: var(--spacing);
    }

    pre {
        overflow-x: auto;
        font-size: 0.9rem;
        background-color: var(--color-gray-100);
        padding: var(--spacing);
        code {
            background-color: transparent;
            padding: 0;
        }
    }

    time {
        display: block;
        margin-bottom: calc(var(--spacing) * 2);
        font-size: var(--text-sm);
        font-weight: bold;
    }

    img {
        object-fit: contain;
        max-height: 450px;
    }

    .tags {
        display: flex;
        gap: calc(var(--spacing) * 2);
        flex-wrap: wrap;
    }

    .tag {
        margin: 0;
        padding: 0 calc(var(--spacing) * 2);
        background: var(--color-gray-200);
        border-radius: var(--radius-xl);
        font-size: var(--text-sm);
        border: 1px solid var(--color-gray-300);
    }

    .youtube-container {
        width: 100%;
        aspect-ratio: 16 / 9;
        margin-bottom: calc(var(--spacing) * 4);
    }

    .callout {
        padding: calc(var(--spacing) * 4);
        margin-bottom: calc(var(--spacing) * 4);
        padding-bottom: 0;
    }

    .callout-tip {
        background-color: var(--color-success-100);
        border: 1px solid var(--color-success-300);
    }

    .callout-warning {
        background-color: var(--color-warning-100);
        border: 1px solid var(--color-warning-300);
    }

    .post-image {
        margin: calc(var(--spacing) * 4) 0;
        img {
            width: 100%;
        }
    }

    .posts {
        font-size: var(--text-sm);
        h3 {
            margin-top: 0;
        }
        .post {
            display: grid;
            grid-template-columns: calc(var(--spacing) * 32) auto;
            gap: calc(var(--spacing) * 4);
            margin-bottom: calc(var(--spacing) * 8);

            @media (min-width: 768px) {
                grid-template-columns: calc(var(--spacing) * 48) auto;
            }
        }
        .post-image {
            margin: 0;
        }
    }

    .featured.posts .post {
        grid-template-columns: 1fr;
        h3 {
            font-size: var(--text-2xl);
        }

        @media (min-width: 1024px) {
            grid-template-columns: 1fr 1fr;
        }
    }
}
</style>
